import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import type { Organization, WebSite, WithContext } from "schema-dts";

import { seoConfig } from "./seo.config";
import { SUPPORTED_LANGS } from "../i18n/i18n";

const DefaultSeo: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as string;

  const title = t("seo.title");
  const description = t("seo.description");
  const locale = t("seo.locale");

  const canonicalUrl = `${seoConfig.siteUrl}/${lang}/`;

  const websiteSchema: WithContext<WebSite> = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: seoConfig.siteName,
    url: seoConfig.siteUrl,
    inLanguage: lang,
  };

  const organizationSchema: WithContext<Organization> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: seoConfig.business.name,
    url: seoConfig.siteUrl,
    logo: seoConfig.defaultImage,
    email: seoConfig.business.email,
    sameAs: seoConfig.business.sameAs,
  };

  const jsonLdScripts = [websiteSchema, organizationSchema].map((schema) => ({
    type: "application/ld+json",
    innerHTML: JSON.stringify(schema),
  }));

  return (
    <Helmet script={jsonLdScripts}>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index,follow,max-image-preview:large" />
      <meta name="author" content={seoConfig.siteName} />
      <link rel="canonical" href={canonicalUrl} />

      {SUPPORTED_LANGS.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${seoConfig.siteUrl}/${l}/`}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${seoConfig.siteUrl}/en/`}
      />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={seoConfig.defaultImage} />
      <meta property="og:locale" content={locale} />

      <meta name="twitter:card" content={seoConfig.twitterCardType} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={seoConfig.defaultImage} />
      <meta name="twitter:site" content={seoConfig.twitterHandle} />
    </Helmet>
  );
};

export default DefaultSeo;
